import React from "react";
import { Phone, Mail, MapPin, ShieldCheck, ExternalLink, Clock } from "lucide-react";
import ContactForm from "../components/ContactForm";

export default function Contact() {
  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Page Header */}
      <section className="bg-black text-white py-16 border-b border-neutral-900 text-center relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/0e9ea851-6d58-4fd2-90d8-0e288f45a857.jpg"
            alt="Kontakt os baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-neutral-400 block mb-2">
            Vi glæder os til at høre fra dig
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight">
            Kontakt os
          </h1>
          <p className="text-sm text-neutral-400 max-w-lg mx-auto leading-relaxed">
            Har du spørgsmål til vores menukort, ønsker du et tilbud på en firmaordning, eller vil du bestille mad til et arrangement? Ring eller skriv til os i dag.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-10">
            {/* Contact Details */}
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#2d5a27] block mb-2">
                Information
              </span>
              <h2 className="text-3xl font-serif font-bold text-neutral-900 tracking-tight">
                Hillerød Smørrebrød
              </h2>

              <div className="space-y-6 mt-8">
                <div className="flex items-start gap-4">
                  <div className="text-neutral-800 pt-1">
                    <Phone size={20} />
                  </div>
                  <div>
                    <a href="tel:48266613" className="text-lg font-serif font-bold text-neutral-900 hover:text-[#2d5a27] transition-colors">
                      48 26 66 13
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-neutral-800 pt-1">
                    <Mail size={20} />
                  </div>
                  <div>
                    <a href="mailto:kontakt@hilleroedsmoerrebroed.dk" className="text-lg font-serif font-bold text-neutral-900 hover:text-[#2d5a27] transition-colors">
                      kontakt@hilleroedsmoerrebroed.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-neutral-800 pt-1">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-base text-neutral-800 font-medium">
                      Frederiksværksgade 63, 3400 Hillerød
                    </p>
                    <a
                      href="https://maps.google.com/?q=Frederiksværksgade+63+3400+Hillerød"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#2d5a27] hover:underline mt-1.5"
                    >
                      <span>Få rutevejledning</span>
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-neutral-50 p-8 border border-neutral-200">
              <div className="flex items-center gap-2 mb-4 border-b border-neutral-200 pb-3">
                <Clock size={18} className="text-[#2d5a27]" />
                <h3 className="font-serif font-bold text-base uppercase tracking-wider text-neutral-900">
                  Åbningstider
                </h3>
              </div>
              <ul className="text-sm text-neutral-600 flex flex-col gap-2.5">
                <li className="flex justify-between">
                  <span>Mandag - Onsdag:</span>
                  <span className="text-neutral-900 font-semibold">08:00 - 14:00</span>
                </li>
                <li className="flex justify-between">
                  <span>Torsdag - Fredag:</span>
                  <span className="text-neutral-900 font-semibold">08:00 - 14:00</span>
                </li>
                <li className="flex justify-between">
                  <span>Lørdag:</span>
                  <span className="text-neutral-900 font-semibold">10:00 - 14:00</span>
                </li>
                <li className="flex justify-between">
                  <span>Søndag:</span>
                  <span className="text-neutral-400">Lukket (Efter aftale)</span>
                </li>
              </ul>
            </div>

            {/* Smiley Report */}
            <div className="bg-white p-6 border border-neutral-100 shadow-md flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheck size={28} className="text-black" />
                <div>
                  <h4 className="font-serif font-bold text-sm text-neutral-900">Fødevarestyrelsens Smiley</h4>
                  <p className="text-xs text-neutral-500">Vi har elite-smiley og overholder alle krav.</p>
                </div>
              </div>
              <a
                href="https://www.findsmiley.dk/1447473"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-emerald-800 hover:bg-emerald-900 text-white transition-colors"
              >
                <span>Se rapport</span>
                <ExternalLink size={12} />
              </a>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <ContactForm subject="Generel henvendelse fra hjemmeside" />
          </div>
        </div>
      </section>
    </div>
  );
}
